import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, BookOpen } from "lucide-react";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { errorMessage } from "../../global-components/src/utils/text";
import { useAuth, useMessage } from "../../global-components/src";
import Spinner from "../../global-components/src/components/spinner/spiner";
import TermsUseText from "../../global-components/src/components/terms-use";
import GuidelinesText from "../../global-components/src/components/guidelines-text";

interface RegisterAccountProps {
  system?: string;
}

export default function RegisterAccount({ system = "Paçoca Oficial" }: RegisterAccountProps) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [user_name, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorsInput, setErrorsInput] = useState<{
    name?: string[]; user_name?: string[]; acceptTerms?: string[];
    email?: string[]; password?: string[];
  }>({});
  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleConfirmation, setIsVisibleConfirmation] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { loginContext } = useAuth();
  const { setMessage } = useMessage();

  useEffect(() => {
    if (password.length > 0 && passwordConfirmation.length > 0 && password !== passwordConfirmation) {
      setErrorsInput(prev => ({ ...prev, password: ["As senhas não conferem"] }));
    } else {
      setErrorsInput(prev => { const e = { ...prev }; delete e.password; return e; });
    }
  }, [password, passwordConfirmation]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setErrorsInput({});
    e.preventDefault();

    try {
      let errors: Record<string, string[]> = {};
      if (!name)          errors.name       = ["O nome é obrigatório."];
      if (!user_name)     errors.user_name  = ["O nome de usuário é obrigatório."];
      if (!email)         errors.email      = ["O e-mail é obrigatório."];
      if (!password)      errors.password   = ["A senha é obrigatória."];
      else if (password.length < 8) errors.password = ["A senha precisa ter 8 caracteres."];
      if (password !== passwordConfirmation) errors.password = ["As senhas não conferem."];
      if (!acceptTerms)   errors.acceptTerms = ["Você precisa aceitar os termos."];

      if (Object.keys(errors).length > 0) throw { response: { data: { errors } } };

      const response = await axios.post(`${PACOCA_API_URL}/register`, {
        name, password, user_name, email,
        password_confirmation: passwordConfirmation,
        terms: acceptTerms,
      });
      loginContext(response.data.access_token, response.data.user, acceptTerms);
      navigate("/");
      setMessage(response.data.message, "success");
    } catch (err: any) {
      if (err?.response?.data.errors) {
        setErrorsInput(err.response.data.errors);
      } else {
        setMessage(errorMessage(err), "error");
      }
    }
    setLoading(false);
  };

  return (
    <div className="auth-root">
      {/* Left – visual panel (same mosaic strip) */}
      <div className="auth-panel auth-panel--visual">
        <div className="auth-mosaic" aria-hidden="true">
          <div className="auth-mosaic-col auth-mosaic-col--1">
            {[
              "/img/img_index/1984.webp",
              "/img/img_index/percy - mar de monstros.jpg",
              "/img/img_index/percy - maldicao do tita.jpg",
              "/img/img_index/percy - o ultimo olimpiano.jpg",
              "/img/img_index/herry - a pedra filosofal.jpg",
              "/img/img_index/harry - a ordem de fenix.jpg",
              "/img/img_index/1984.webp",
              "/img/img_index/percy - mar de monstros.jpg",
              "/img/img_index/percy - maldicao do tita.jpg",
              "/img/img_index/percy - o ultimo olimpiano.jpg",
              "/img/img_index/herry - a pedra filosofal.jpg",
              "/img/img_index/harry - a ordem de fenix.jpg",
            ].map((s, i) => <img key={i} src={s} alt="" className="auth-mosaic-img" />)}
          </div>
          <div className="auth-mosaic-col auth-mosaic-col--2">
            {[
              "/img/img_index/harry - enigma do principe.jpg",
              "/img/img_index/assim que acaba.jpeg",
              "/img/img_index/percy - e os olimpianos.jpg",
              "/img/img_index/biblioteca da meia noite.jpg",
              "/img/img_index/diario de anne frank.jpg",
              "/img/img_index/harry - prsioneiro de azkaban.jpg",
              "/img/img_index/harry - enigma do principe.jpg",
              "/img/img_index/assim que acaba.jpeg",
              "/img/img_index/percy - e os olimpianos.jpg",
              "/img/img_index/biblioteca da meia noite.jpg",
              "/img/img_index/diario de anne frank.jpg",
              "/img/img_index/harry - prsioneiro de azkaban.jpg",
            ].map((s, i) => <img key={i} src={s} alt="" className="auth-mosaic-img" />)}
          </div>
        </div>
        <div className="auth-panel-overlay" />
        <div className="auth-panel-brand">
          <BookOpen size={28} strokeWidth={1.5} />
          <span>ReadBooks</span>
        </div>
        <p className="auth-panel-quote">
          "Não há navio que nos leve tão longe quanto um livro."
        </p>
      </div>

      {/* Right – form panel */}
      <div className="auth-panel auth-panel--form">
        <div className="auth-form-inner" style={{ maxWidth: 480 }}>
          <div className="auth-form-header">
            <h1 className="auth-title">Criar conta</h1>
            <p className="auth-description">Comece a registrar sua jornada literária hoje</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* Name + Email row */}
            <div className="auth-form-grid">
              <div className="auth-field">
                <label className="auth-label" htmlFor="reg-name">Nome</label>
                <input
                  id="reg-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`auth-input ${errorsInput.name ? 'auth-input--error' : ''}`}
                  autoComplete="name"
                  placeholder="Seu nome"
                />
                {errorsInput.name && <p className="auth-error">{errorsInput.name[0]}</p>}
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="reg-email">E-mail</label>
                <input
                  id="reg-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`auth-input ${errorsInput.email ? 'auth-input--error' : ''}`}
                  autoComplete="email"
                  placeholder="seu@email.com"
                />
                {errorsInput.email && <p className="auth-error">{errorsInput.email[0]}</p>}
              </div>
            </div>

            {/* Username */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="reg-username">Nome de usuário</label>
              <div className="auth-input-wrapper" style={{ display: 'flex' }}>
                <span style={{
                  display: 'flex', alignItems: 'center', padding: '0 12px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRight: 'none',
                  borderRadius: '10px 0 0 10px',
                  color: 'rgba(250,248,244,0.35)',
                  fontSize: '0.9rem',
                  flexShrink: 0,
                }}>@</span>
                <input
                  id="reg-username"
                  type="text"
                  value={user_name}
                  onChange={(e) => setUserName(e.target.value)}
                  className={`auth-input ${errorsInput.user_name ? 'auth-input--error' : ''}`}
                  style={{ borderRadius: '0 10px 10px 0', flex: 1 }}
                  autoComplete="username"
                  placeholder="seu_usuario"
                />
              </div>
              {errorsInput.user_name
                ? <p className="auth-error">{errorsInput.user_name[0]}</p>
                : <p className="auth-hint">Letras, números e sublinhados (_) apenas</p>}
            </div>

            {/* Password row */}
            <div className="auth-form-grid">
              <div className="auth-field">
                <label className="auth-label" htmlFor="reg-password">Senha</label>
                <div className="auth-input-wrapper">
                  <input
                    id="reg-password"
                    type={isVisible ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`auth-input ${errorsInput.password ? 'auth-input--error' : ''}`}
                    placeholder="••••••••"
                  />
                  <button type="button" className="auth-eye" onClick={() => setIsVisible(!isVisible)} aria-label="Mostrar senha">
                    {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="reg-confirm">Confirmar</label>
                <div className="auth-input-wrapper">
                  <input
                    id="reg-confirm"
                    type={isVisibleConfirmation ? "text" : "password"}
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    className={`auth-input ${errorsInput.password ? 'auth-input--error' : ''}`}
                    placeholder="••••••••"
                  />
                  <button type="button" className="auth-eye" onClick={() => setIsVisibleConfirmation(!isVisibleConfirmation)} aria-label="Mostrar confirmação">
                    {isVisibleConfirmation ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {errorsInput.password && (
              <p className="auth-error" style={{ marginTop: '-0.75rem' }}>{errorsInput.password[0]}</p>
            )}
            <p className="auth-hint" style={{ marginTop: '-0.5rem' }}>Mínimo de 8 caracteres</p>

            {/* Terms */}
            <div className="auth-field">
              <label className="auth-checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={() => setAcceptTerms(!acceptTerms)}
                  className="auth-checkbox"
                />
                <span className="auth-terms-text">
                  Concordo com os{" "}
                  <button type="button" className="auth-terms-btn" data-bs-toggle="modal" data-bs-target="#modal-termos-de-uso">
                    termos de uso
                  </button>{" "}
                  e as{" "}
                  <button type="button" className="auth-terms-btn" data-bs-toggle="modal" data-bs-target="#modal-politicas-de-privacidade">
                    diretrizes
                  </button>{" "}
                  do {system}
                </span>
              </label>
              {errorsInput.acceptTerms && <p className="auth-error">{errorsInput.acceptTerms[0]}</p>}
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? <Spinner /> : "Criar conta"}
            </button>

            <p className="auth-footer-text">
              Já tem uma conta?{" "}
              <Link to="/login" className="auth-link">Fazer login</Link>
            </p>
          </form>
        </div>
      </div>

      {/* Modal – Terms */}
      <div className="modal fade auth-modal" style={{ overflow: "hidden" }} id="modal-termos-de-uso" tabIndex={-1} aria-labelledby="modal-termos-de-uso" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Termos de uso</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fechar" />
            </div>
            <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <TermsUseText system={system} />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Fechar</button>
              <button type="button" className="btn btn-blue" data-bs-dismiss="modal" onClick={() => setAcceptTerms(true)}>Aceitar</button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal – Guidelines */}
      <div className="modal fade auth-modal" style={{ overflow: "hidden" }} id="modal-politicas-de-privacidade" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Diretrizes</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fechar" />
            </div>
            <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <GuidelinesText system={system} />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Fechar</button>
              <button type="button" className="btn btn-blue" data-bs-dismiss="modal" onClick={() => setAcceptTerms(true)}>Aceitar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}